import { CheckIcon } from "lucide-react";
import { useState, useEffect } from "react";
import { Stepper, StepperIndicator, StepperItem, StepperSeparator, StepperTitle, StepperTrigger } from "../ui/stepper";

type Step = {
	step: number;
	title: string;
};

type StepperComponentProps = {
	steps: Step[];
	stepSelected?: number;
	onStepSelected?: (step: number) => void;
	isStepDisabled?: (step: number) => boolean;
};

export const VerticalStepper = ({
	steps,
	stepSelected,
	onStepSelected,
	isStepDisabled
}: StepperComponentProps) => {
	const [activeStep, setActiveStep] = useState<number>(stepSelected ?? 1);

	useEffect(() => {
		if (typeof stepSelected === "number" && stepSelected !== activeStep) {
			setActiveStep(stepSelected);
		}
	}, [stepSelected]);

	const handleStepChange = (step: number) => {
		setActiveStep(step);
		onStepSelected?.(step);
	};

	return (
		<div className="space-y-8 text-center">
			<Stepper
				value={activeStep}
				onValueChange={handleStepChange}
				orientation="vertical"
			>
				{steps.map(({ step, title }) => {
					const isCompleted = step < activeStep;
					const isActive = step === activeStep;

					return (
						<StepperItem
							key={step}
							step={step}
							className="relative items-start not-last:flex-1"
						>
							<StepperTrigger
							 	className="items-start rounded pb-12 last:pb-0" 
							 	disabled={isStepDisabled?.(step) ?? false}
								onClick={async (e) => {
									e.preventDefault();
									const disabled = await isStepDisabled?.(step);
									if (!disabled) {
										handleStepChange(step);
									}
								}}>
								<StepperIndicator
									className="bg-white border border-gray"
									asChild
								>
									{isCompleted ? (
										<CheckIcon size={16} aria-hidden="true" />
									) : isActive ? (
										<span className="block size-2 rounded-full bg-white" />
									) : (
										<span className="block size-2 rounded-full bg-gray-300" />
									)}
								</StepperIndicator>
								<div className="mt-0.5 px-2 text-left">
									<StepperTitle>{title}</StepperTitle>
								</div>
							</StepperTrigger>
							{step < steps.length && (
								<StepperSeparator className="absolute inset-y-0 top-[calc(1.5rem+0.125rem)] left-3 -order-1 m-0 -translate-x-1/2 group-data-[orientation=horizontal]/stepper:w-[calc(100%-1.5rem-0.25rem)] group-data-[orientation=horizontal]/stepper:flex-none group-data-[orientation=vertical]/stepper:h-[calc(100%-1.5rem-0.25rem)]" />
							)}
						</StepperItem>
					);
				})}
			</Stepper>
		</div>
	);
};

export default VerticalStepper;